import Modal from "@/shared/ui/components/modal";
import Button from "@/shared/ui/components/button";
import InputText from "@/shared/ui/components/input-text";
import { useTranslation } from "react-i18next";
import { Processing } from "../types";
import {
  emitPaymentSubmissionTracking,
  emitModalTracking,
} from "@/shared/tracking/analytics";
import type { CheckoutSession } from "@/types";

interface CvvModalProps {
  isOpen: boolean;
  onClose: () => void;
  cvv: string;
  onCvvChange: (cvv: string) => void;
  onSubmit: () => Promise<void>;
  processing: Processing;
  session?: CheckoutSession;
  selectedPaymentMethodId?: string;
  isWalletSelected: boolean;
  documentSignatureIsPending: boolean;
  isProcessingPayment: boolean;
}

/**
 * Component for CVV input modal
 * Single responsibility: Rendering CVV modal UI
 */
export function CvvModal({
  isOpen,
  onClose,
  cvv,
  onCvvChange,
  onSubmit,
  processing,
  session,
  selectedPaymentMethodId,
  isWalletSelected,
  documentSignatureIsPending,
  isProcessingPayment,
}: CvvModalProps) {
  void documentSignatureIsPending; // reserved for future use
  const { t } = useTranslation();

  if (!isOpen || isProcessingPayment) return null;

  const handleClose = () => {
    if (session) {
      emitModalTracking("cvv_modal_closed", session, {
        modal_name: "cvv",
      });
    }
    onClose();
  };

  const handleSubmit = async () => {
    try {
      if (session) {
        emitPaymentSubmissionTracking("cvv_submission_started", session, {
          payment_method_type: "card",
          card_id: selectedPaymentMethodId,
          wallet_selected: isWalletSelected,
        });
      }

      await onSubmit();

      if (session) {
        emitPaymentSubmissionTracking("cvv_submission_completed", session, {
          payment_method_type: "card",
          card_id: selectedPaymentMethodId,
          wallet_selected: isWalletSelected,
        });
      }
    } catch (error) {
      if (session) {
        emitPaymentSubmissionTracking("cvv_submission_failed", session, {
          payment_method_type: "card",
          card_id: selectedPaymentMethodId,
          wallet_selected: isWalletSelected,
          error_message:
            error instanceof Error ? error.message : String(error),
        });
      }
      throw error;
    }
  };

  return (
    <Modal onClose={handleClose} showCloseButton>
      <div className="px-6 pt-6 pb-3" data-testid="cvv-modal">
        <div className="flex items-center justify-between">
          <div className="w-[40px]"></div>
          <h3 className="text-xl font-semibold text-gray-900 pt-6" data-testid="cvv-modal-title">
            {t("payment_confirmation.cvv_modal_title")}
          </h3>
          <div className="w-[40px]"></div>
        </div>
      </div>

      <div className="p-6 overflow-y-auto flex-1">
        <div>
          <InputText
            type="text"
            placeholder={t("payment_confirmation.cvv_modal_placeholder")}
            value={cvv}
            onChange={(e) => onCvvChange(e.target.value)}
            strict={/^[0-9]*$/}
            maxLength={4}
            data-testid="cvv-modal-input"
          />
        </div>
      </div>

      <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6">
        <Button
          size="large"
          onClick={handleSubmit}
          disabled={processing === Processing.SUBMIT || !cvv.trim()}
          loading={processing === Processing.SUBMIT}
          data-testid="cvv-modal-submit-button"
        >
          {t("payment_confirmation.cvv_modal_pay_button")}
        </Button>
      </div>
    </Modal>
  );
}
